<!-- Modern Admin Dashboard - Mental Health Chatbot System -->
<div class="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50 to-secondary-50">
  
  <!-- Header Section -->
  <div class="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-sm bg-white/90">
    <div class="max-w-7xl mx-auto px-6 py-4">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            Analytics Dashboard
          </h1>
          <p class="text-sm text-slate-600 mt-1">Mental Health Chatbot Performance Overview</p>
        </div>
        <div class="flex items-center gap-3">
          <button (click)="loadAnalytics()" class="px-4 py-2 text-sm font-medium text-slate-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200">
            <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Refresh
          </button>
          <div class="h-8 w-px bg-slate-200"></div>
          <div class="flex items-center gap-2 px-3 py-2 bg-primary-50 text-primary-700 rounded-lg">
            <div class="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
            <span class="text-xs font-medium">Live</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="max-w-7xl mx-auto px-6 py-8">
    
    <!-- Loading State -->
    <div *ngIf="!summary && !error" class="flex items-center justify-center py-20">
      <div class="text-center">
        <div class="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-slate-600 font-medium">Loading analytics...</p>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="error" class="flex items-center justify-center py-20">
      <div class="text-center max-w-md">
        <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-slate-800 mb-2">Unable to Load Analytics</h3>
        <p class="text-slate-600 mb-3">{{error}}</p>
        <p class="text-xs text-slate-500 mb-4">Will auto-retry in 30 seconds. You can also try syncing the database.</p>
        <div class="flex gap-3 justify-center flex-wrap">
          <button (click)="loadAnalytics()" class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            Retry Now
          </button>
          <button (click)="syncAnalytics()" [disabled]="syncing" class="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60">
            {{ syncing ? 'Syncing...' : 'Sync Database' }}
          </button>
        </div>
        <p *ngIf="syncMessage" class="text-sm mt-3" [class.text-emerald-600]="!syncing" [class.text-red-600]="syncMessage.includes('failed')">{{syncMessage}}</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div *ngIf="summary" class="space-y-6 animate-fadeIn">
      
      <!-- Hero Metrics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        <!-- Total Conversations Card -->
        <div class="group relative bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 overflow-hidden">
          <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
              </div>
              <span class="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">Today</span>
            </div>
            <div class="text-3xl font-bold mb-1">{{summary.today.total_conversations || 0}}</div>
            <div class="text-primary-100 text-sm font-medium">Total Conversations</div>
            <div class="mt-3 flex items-center text-xs">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clip-rule="evenodd"></path>
              </svg>
              <span class="opacity-90">Active sessions</span>
            </div>
          </div>
        </div>

        <!-- Messages Card -->
        <div class="group relative bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 overflow-hidden">
          <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                </svg>
              </div>
              <span class="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">Today</span>
            </div>
            <div class="text-3xl font-bold mb-1">{{summary.today.total_messages || 0}}</div>
            <div class="text-secondary-100 text-sm font-medium">Total Messages</div>
            <div class="mt-3 flex items-center text-xs">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"></path>
                <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"></path>
              </svg>
              <span class="opacity-90">Exchanged today</span>
            </div>
          </div>
        </div>

        <!-- FAQ Matches Card -->
        <div class="group relative bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 overflow-hidden">
          <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span class="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">Today</span>
            </div>
            <div class="text-3xl font-bold mb-1">{{summary.today.faq_matches || 0}}</div>
            <div class="text-emerald-100 text-sm font-medium">FAQ Matches</div>
            <div class="mt-3 flex items-center text-xs">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span class="opacity-90">Instant answers</span>
            </div>
          </div>
        </div>

        <!-- AI Responses Card -->
        <div class="group relative bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 overflow-hidden">
          <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
          <div class="relative">
            <div class="flex items-center justify-between mb-4">
              <div class="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <span class="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">Today</span>
            </div>
            <div class="text-3xl font-bold mb-1">{{summary.today.ai_responses || 0}}</div>
            <div class="text-orange-100 text-sm font-medium">AI Responses</div>
            <div class="mt-3 flex items-center text-xs">
              <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"></path>
              </svg>
              <span class="opacity-90">Gemini powered</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts and Analytics Section -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        <!-- Main Chart - 2/3 width -->
        <div class="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-bold text-slate-800">Conversation Trends</h3>
              <p class="text-sm text-slate-500 mt-1">Last 7 days activity overview</p>
            </div>
            <div class="flex gap-2">
              <button class="px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">7D</button>
              <button class="px-3 py-1.5 text-xs font-medium text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">30D</button>
            </div>
          </div>
          <div class="relative">
            <canvas #trendCanvas class="w-full" height="280"></canvas>
          </div>
        </div>

        <!-- Performance Stats - 1/3 width -->
        <div class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <h3 class="text-lg font-bold text-slate-800 mb-6">Performance</h3>
          
          <!-- Sentiment Score -->
          <div class="mb-6">
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm font-medium text-slate-600">Avg. Sentiment</span>
              <span class="text-lg font-bold text-indigo-600">{{summary.today.avg_sentiment_score || 0 | number:'1.2-2'}}</span>
            </div>
            <div class="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div class="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-500" 
                   [style.width.%]="(summary.today.avg_sentiment_score || 0) * 100"></div>
            </div>
            <p class="text-xs text-slate-500 mt-2">Customer satisfaction level</p>
          </div>

          <!-- Response Distribution -->
          <div class="space-y-4">
            <div class="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <div class="text-xs text-emerald-600 font-medium">FAQ Matched</div>
                  <div class="text-sm font-bold text-emerald-700">{{summary.today.faq_matches || 0}}</div>
                </div>
              </div>
              <div class="text-xs text-emerald-600 font-bold">
                {{summary.today.total_messages > 0 ? ((summary.today.faq_matches / summary.today.total_messages) * 100 | number:'1.0-0') : 0}}%
              </div>
            </div>

            <div class="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"></path>
                  </svg>
                </div>
                <div>
                  <div class="text-xs text-amber-600 font-medium">AI Generated</div>
                  <div class="text-sm font-bold text-amber-700">{{summary.today.ai_responses || 0}}</div>
                </div>
              </div>
              <div class="text-xs text-amber-600 font-bold">
                {{summary.today.total_messages > 0 ? ((summary.today.ai_responses / summary.today.total_messages) * 100 | number:'1.0-0') : 0}}%
              </div>
            </div>

            <div class="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <svg class="w-5 h-5 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <div class="text-xs text-slate-600 font-medium">Fallback</div>
                  <div class="text-sm font-bold text-slate-700">{{summary.today.fallback_responses || 0}}</div>
                </div>
              </div>
              <div class="text-xs text-slate-600 font-bold">
                {{summary.today.total_messages > 0 ? ((summary.today.fallback_responses / summary.today.total_messages) * 100 | number:'1.0-0') : 0}}%
              </div>
            </div>
          </div>

          <!-- Complaints Today -->
          <div class="mt-6 pt-6 border-t border-slate-100">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-slate-600">Complaints Created</span>
              <span class="text-2xl font-bold text-rose-600">{{summary.today.complaints_created || 0}}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions & System Health -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        <!-- Quick Actions Panel -->
        <div class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <h3 class="text-lg font-bold text-slate-800 mb-6">Quick Actions</h3>
          <div class="grid grid-cols-2 gap-4">
            <button routerLink="/admin/faq-management" class="group p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 rounded-xl border border-indigo-200 transition-all duration-300 hover:scale-105">
              <div class="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="text-sm font-semibold text-indigo-900">Manage FAQs</div>
              <div class="text-xs text-indigo-600 mt-1">Edit knowledge base</div>
            </button>

            <button routerLink="/admin/complaints" class="group p-4 bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl border border-purple-200 transition-all duration-300 hover:scale-105">
              <div class="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="text-sm font-semibold text-purple-900">View Complaints</div>
              <div class="text-xs text-purple-600 mt-1">Ticket management</div>
            </button>

            <button (click)="loadAnalytics()" class="group p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 rounded-xl border border-emerald-200 transition-all duration-300 hover:scale-105">
              <div class="w-12 h-12 bg-emerald-500 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
              </div>
              <div class="text-sm font-semibold text-emerald-900">Refresh Data</div>
              <div class="text-xs text-emerald-600 mt-1">Reload analytics</div>
            </button>

            <button routerLink="/admin/settings" class="group p-4 bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 rounded-xl border border-amber-200 transition-all duration-300 hover:scale-105">
              <div class="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
              </div>
              <div class="text-sm font-semibold text-amber-900">Settings</div>
              <div class="text-xs text-amber-600 mt-1">System config</div>
            </button>
          </div>
        </div>

        <!-- System Health -->
        <div class="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
          <h3 class="text-lg font-bold text-slate-800 mb-6">System Health</h3>
          
          <div class="space-y-4">
            <!-- API Status -->
            <div class="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <div class="w-3 h-3 bg-emerald-500 rounded-full"></div>
                  <div class="absolute inset-0 w-3 h-3 bg-emerald-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <div>
                  <div class="text-sm font-semibold text-slate-800">Gemini API</div>
                  <div class="text-xs text-slate-500">Google AI Service</div>
                </div>
              </div>
              <span class="text-xs font-bold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">Operational</span>
            </div>

            <!-- Database Status -->
            <div class="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <div class="w-3 h-3 bg-emerald-500 rounded-full"></div>
                  <div class="absolute inset-0 w-3 h-3 bg-emerald-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <div>
                  <div class="text-sm font-semibold text-slate-800">Database</div>
                  <div class="text-xs text-slate-500">MySQL Connection</div>
                </div>
              </div>
              <span class="text-xs font-bold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">Connected</span>
            </div>

            <!-- NLP Service -->
            <div class="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <div class="w-3 h-3 bg-emerald-500 rounded-full"></div>
                  <div class="absolute inset-0 w-3 h-3 bg-emerald-500 rounded-full animate-ping opacity-75"></div>
                </div>
                <div>
                  <div class="text-sm font-semibold text-slate-800">NLP Engine</div>
                  <div class="text-xs text-slate-500">FAQ Matching Service</div>
                </div>
              </div>
              <span class="text-xs font-bold text-emerald-600 bg-emerald-100 px-3 py-1 rounded-full">Active</span>
            </div>

            <!-- Uptime -->
            <div class="mt-6 pt-6 border-t border-slate-100">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-slate-600">System Uptime</span>
                <span class="text-sm font-bold text-slate-800">99.9%</span>
              </div>
              <div class="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-emerald-500 to-emerald-400 h-2 rounded-full" style="width: 99.9%"></div>
              </div>
              <p class="text-xs text-slate-500 mt-2">Last 30 days average</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>

<style>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out;
}
</style>
